import React, { createContext, useContext, ReactNode, useEffect, useState } from 'react'
import { fetchAuthSession } from 'aws-amplify/auth'

const IS_MOCK = process.env.REACT_APP_MOCK_AUTH === 'true';

interface User {
  username?: string;
  signInDetails?: {
    loginId?: string;
  };
  signInUserSession?: {
    accessToken?: {
      payload?: {
        'custom:role'?: string;
        'cognito:groups'?: string[];
      };
    };
    idToken?: {
      payload?: {
        'custom:role'?: string;
      };
    };
  };
}

interface AuthContextType {
  user: User | null
  signOut: () => void
  isAdmin: boolean
  isManager: boolean
  userRole: 'user' | 'manager' | 'admin'
  loading: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
  user: User | null
  signOut: () => void
}

export function AuthProvider({ children, user, signOut }: AuthProviderProps) {
  const [userRole, setUserRole] = useState<'user' | 'manager' | 'admin'>('user');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkUserRole = async () => {
      try {
        if (!user) {
          setUserRole('user');
          setLoading(false);
          return;
        }

        let groups: string[] = [];

        if (IS_MOCK) {
          const { mockFetchAuthSession } = await import('../mockAuth');
          const session = await mockFetchAuthSession();
          const groupsClaim = session.tokens?.accessToken?.payload?.['cognito:groups'];
          groups = Array.isArray(groupsClaim) ? (groupsClaim as unknown as string[]) : [];
        } else {
          const session = await fetchAuthSession();
          const accessToken = session.tokens?.accessToken;
          const groupsClaim = accessToken?.payload?.['cognito:groups'];
          groups = Array.isArray(groupsClaim) ? (groupsClaim as unknown as string[]) : [];
        }

        let role: 'user' | 'manager' | 'admin' = 'user';
        if (groups.includes('admin')) role = 'admin';
        else if (groups.includes('manager')) role = 'manager';

        setUserRole(role);
      } catch (error) {
        console.error('Error checking user role in AuthContext:', error);
        setUserRole('user');
      } finally {
        setLoading(false);
      }
    };

    checkUserRole();
  }, [user]);

  const isAdmin = userRole === 'admin';
  const isManager = userRole === 'manager';

  const value = {
    user,
    signOut,
    isAdmin,
    isManager,
    userRole,
    loading
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}